import { useState, type ComponentProps } from 'react';
import type { Rol } from '@medchoice/shared';
import { Entrada, Icono, type NombreIcono } from '../../../ui';
import { fuerzaDeClave } from '../../../forms/validacion';

export const INICIO_POR_ROL: Record<Rol, string> = {
  paciente: '/inicio',
  profesional: '/pro/agenda',
  administrador: '/admin',
};

/** Solo se vuelve a la ruta pedida si esa cuenta puede entrar ahí. */
export function destinoDespuesDeIngresar(rol: Rol, volverA: string | null): string {
  if (!volverA) return INICIO_POR_ROL[rol];
  const esDelPanel = volverA.startsWith('/pro/') || volverA.startsWith('/admin');
  const correspondeAlRol = (rol === 'profesional' && volverA.startsWith('/pro/'))
    || (rol === 'administrador' && volverA.startsWith('/admin'))
    || (rol === 'paciente' && !esDelPanel);
  return correspondeAlRol ? volverA : INICIO_POR_ROL[rol];
}

// El ojito evita escribir a ciegas, la primera causa de "la clave no me anda".
export function CampoClave(props: Omit<ComponentProps<typeof Entrada>, 'type' | 'extremo'>) {
  const [visible, setVisible] = useState(false);
  return (
    <Entrada
      {...props}
      type={visible ? 'text' : 'password'}
      extremo={(
        <button
          type="button"
          className="acceso__ojo"
          onClick={() => setVisible((v) => !v)}
          aria-label={visible ? 'Ocultar contraseña' : 'Mostrar contraseña'}
        >
          <Icono nombre={visible ? 'eye-off' : 'eye'} tamano={17} />
        </button>
      )}
    />
  );
}

const TEXTO_FUERZA = ['Muy débil', 'Débil', 'Aceptable', 'Buena', 'Fuerte'];

export function MedidorDeFuerza({ clave }: { clave: string }) {
  if (!clave) return null;
  const nivel = fuerzaDeClave(clave);
  return (
    <div className={`medidor medidor--${nivel}`} aria-live="polite">
      <div className="medidor__barras">
        {[0, 1, 2, 3].map((i) => <span key={i} className={i < nivel ? 'medidor__barra--llena' : undefined} />)}
      </div>
      <span className="medidor__texto">{TEXTO_FUERZA[nivel]}</span>
    </div>
  );
}

interface Contexto { icono: NombreIcono; titulo: string; cuerpo: string }

function contextoDeVuelta(volverA: string): Contexto {
  if (volverA.startsWith('/reservar/')) {
    return {
      icono: 'calendar', titulo: 'Estás reservando un turno',
      cuerpo: 'Entrá o creá tu cuenta y volvés directo a elegir el horario. No perdés lo que veníamos haciendo.',
    };
  }
  if (volverA.startsWith('/mis-turnos') || volverA.startsWith('/turno/')) {
    return { icono: 'calendar-days', titulo: 'Para ver tus turnos, entrá a tu cuenta', cuerpo: 'Después de ingresar te llevamos directo ahí.' };
  }
  if (volverA.startsWith('/resena/')) {
    return { icono: 'star', titulo: 'Para dejar tu reseña, entrá a tu cuenta', cuerpo: 'Solo puede reseñar quien se atendió, por eso te pedimos que ingreses.' };
  }
  if (volverA.startsWith('/pro/')) {
    return { icono: 'stethoscope', titulo: 'Entrá con tu cuenta profesional', cuerpo: 'Después de ingresar volvés a la sección que estabas viendo.' };
  }
  if (volverA.startsWith('/admin')) {
    return { icono: 'shield-check', titulo: 'Entrá con la cuenta de administración', cuerpo: 'Después de ingresar volvés a la sección que estabas viendo.' };
  }
  return { icono: 'log-in', titulo: 'Entrá para continuar', cuerpo: 'Después de ingresar volvés a donde estabas.' };
}

export function AvisoDeContexto({ volverA }: { volverA: string | null }) {
  if (!volverA) return null;
  const { icono, titulo, cuerpo } = contextoDeVuelta(volverA);
  return (
    <div className="acceso__contexto">
      <Icono nombre={icono} tamano={18} />
      <div>
        <strong>{titulo}</strong>
        <p>{cuerpo}</p>
      </div>
    </div>
  );
}
